import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useLocation, matchPath } from 'react-router-dom'
import { useAuth } from '@core/contexts/AuthContext'
import { useModulosVisibles } from '@core/hooks/useModulosVisibles'
import { veCodigoDeModulo } from '@core/lib/permisoModulo'
import { getAllModules } from '@modules/index'
import { destinoTrasLogin, estadoParaVolver, rutasEnPortalDe, rutaEnPortal, esRutaDelPortal } from '@core/lib/rutas'

// Core pages (siempre cargadas — son livianas y críticas para el primer render)
import LoginPage from '@core/pages/LoginPage'
import Layout from '@core/components/Layout'
import { ErrorBoundaryDeRuta } from '@core/components/ErrorBoundary'  // V3.7.3
import MFAGate from '@core/components/MFAGate'  // SEC-B3 — bloquea consultores sin segundo factor

// Registro self-service (lazy — solo se carga si navegan a /registro)
const RegistroPage = lazy(() => import('@core/pages/RegistroPage'))
// Setear contraseña inicial (lazy — pública, /setup-password?token=XXX)
const SetupPasswordPage = lazy(() => import('@core/pages/SetupPasswordPage'))
// Links de mail de Firebase Auth (verificar mail, reset, deshacer cambio de
// mail) — lazy, pública, /auth/accion?mode=…&oobCode=… (Plataforma A3)
const AccionAuthPage = lazy(() => import('@core/pages/AccionAuthPage'))

// Core lazy (no críticas para el primer render)
const DashboardPage = lazy(() => import('@core/pages/DashboardPage'))
const MiCuentaPage = lazy(() => import('@core/pages/MiCuentaPage'))
const AdminPage = lazy(() => import('@core/pages/AdminPage'))
// Documentación — espejo de la plataforma en modo explicación (solo admin).
// Ruta core (no pasa por el registry de módulos): la página gatea por isAdmin().
const DocumentacionPage = lazy(() => import('@modules/documentacion/DocumentacionPage'))

// Portal Cliente (lazy — se carga solo si el usuario es cliente)
const PortalLayout = lazy(() => import('@portal/PortalLayout'))
const PortalHomePage = lazy(() => import('@portal/pages/PortalHomePage'))
const PortalMensajesPage = lazy(() => import('@portal/pages/PortalMensajesPage'))
const PortalMiEquipoPage = lazy(() => import('@portal/pages/PortalMiEquipoPage'))
const PortalEmpresasPage = lazy(() => import('@portal/pages/PortalEmpresasPage'))
const PortalTrabajoPage = lazy(() => import('@portal/pages/PortalTrabajoPage'))
const PortalProductosPage = lazy(() => import('@portal/pages/PortalProductosPage'))
const PortalCuadroGeneralPage = lazy(() => import('@portal/pages/PortalCuadroGeneralPage'))
const PortalDocumentosPage = lazy(() => import('@portal/pages/PortalDocumentosPage'))
const PortalFormacionPage = lazy(() => import('@portal/pages/PortalFormacionPage'))
const PortalReportesPage = lazy(() => import('@portal/pages/PortalReportesPage'))
const PortalEstudiosPage = lazy(() => import('@portal/pages/PortalEstudiosPage'))
// PortalContactosPage removido — contactos deprecado
// v2 (#49 B9.2-c.2) — UsuariosPage también se renderiza dentro del portal
// (/portal/usuarios) para que el cliente Owner pueda crear/editar colaboradores.
// La misma página detecta `userDoc.tipo === 'cliente'` y restringe la UI.
const UsuariosPage = lazy(() => import('@modules/gestion/pages/UsuariosPage'))

// ── Spinner global para Suspense ──
function PageSpinner() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-[3px] border-transparent border-t-brand-accent rounded-full animate-spin" />
    </div>
  )
}

function FullSpinner() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-brand-bg z-50">
      <div className="w-9 h-9 border-[3px] border-transparent border-t-brand-accent rounded-full animate-spin" />
    </div>
  )
}

// ── Route guards ──
// IMPORTANTE: los guards usan isRealCliente() para que la vista simulada
// (role switcher del super admin) no provoque redirecciones.
function ConsultorRoute({ children }) {
  const { user, loading, isRealCliente } = useAuth()
  const location = useLocation()
  if (loading) return <FullSpinner />
  if (!user) return <Navigate to="/login" replace state={estadoParaVolver(location)} />
  if (isRealCliente()) return <Navigate to={destinoDeCliente(location)} replace />
  return children
}

function ClienteRoute({ children }) {
  const { user, loading, isRealCliente } = useAuth()
  const location = useLocation()
  if (loading) return <FullSpinner />
  if (!user) return <Navigate to="/login" replace state={estadoParaVolver(location)} />
  if (!isRealCliente()) return <Navigate to="/" replace />
  return children
}

// Paso 5 de Eventos (21-sep-2026): las rutas del Club que cada módulo declara
// `enPortal: true` existen en los dos árboles. Un CLIENTE que cae en la del
// staff (el link único del mail, `/eventos/e1?compra=…`) va a su gemela del
// portal con la query intacta; cualquier otra, a `/portal` como siempre.
const RUTAS_EN_PORTAL = rutasEnPortalDe(getAllModules())
const destinoDeCliente = (location) => rutaEnPortal(location.pathname, location.search, RUTAS_EN_PORTAL, matchPath) || '/portal'

// V3.7.3 — ErrorBoundary por route. Si una página rompe en render, el resto
// de la app sigue viva. El error queda loggeado en clientLogs (Firestore).
// El boundary se resetea solo al cambiar la URL (`ErrorBoundaryDeRuta`).
const wrap = (scope, Component) => (
  <ErrorBoundaryDeRuta scope={scope}>
    <Suspense fallback={<PageSpinner />}>
      <Component />
    </Suspense>
  </ErrorBoundaryDeRuta>
)

// ── Genera las <Route> de todos los módulos ──
// Lee las rutas declaradas en cada config.js y las renderiza con Suspense.
// Las rutas públicas (como conectar-ml/:token) se registran fuera del Layout.
// modulosVisibles: array de IDs de módulos visibles para el usuario actual.
// veCodigo(codigo): si el rol ve el código de permiso de una ruta que declara
// `permiso` (report OGxaSGmoUC7El0bYnRZW: ver el módulo por UN código no abre
// todas sus pantallas). Sin montar, la URL cae al catch-all como la de un
// módulo no visible. Los hijos heredan el gate del padre.
function buildModuleRoutes(modulosVisibles, veCodigo) {
  const modules = getAllModules()
  const internalRoutes = []
  const publicRoutes = []
  const portalRoutes = []

  for (const mod of modules) {
    if (!mod.routes) continue
    // Filtrar: solo módulos visibles para el usuario (dos capas).
    // OJO: el filtro aplica SOLO a rutas internas. Las rutas públicas
    // (route.public, ej: /conectar/:token) deben registrarse SIEMPRE —
    // las abre gente sin sesión (clientes externos), que por definición
    // no tiene módulos visibles. Antes este filtro estaba arriba del loop
    // y el link público al cliente terminaba redirigido a /login.
    const modVisible = modulosVisibles.includes(mod.id)
    for (const route of mod.routes) {
      const Component = route.component
      if (!Component) continue

      const element = wrap(`${mod.id}:${route.path}`, Component)

      // Paso 5: la gemela del portal se registra SIN condición de módulo, como
      // las demás rutas del portal (para poder linkearla desde un mail); el
      // gate de pantalla vive en la página y la frontera real en el server.
      if (esRutaDelPortal(route)) {
        portalRoutes.push(
          <Route key={`portal-${mod.id}-${route.path}`} path={route.path} element={wrap(`portal:${mod.id}:${route.path}`, Component)} />
        )
      }
      if (route.public) {
        publicRoutes.push(
          <Route key={`${mod.id}-${route.path}`} path={route.path} element={element} />
        )
      } else if (!modVisible || (route.permiso && !veCodigo(route.permiso))) {
        continue
      } else if (route.children && route.children.length) {
        // Plan IA Paso 7 — rutas anidadas: el componente padre es un layout
        // (renderiza <Outlet/>); cada hijo es una sub-sección con su propia URL.
        internalRoutes.push(
          <Route key={`${mod.id}-${route.path}`} path={route.path} element={element}>
            {route.children.map(child => {
              const ChildComponent = child.component
              if (!ChildComponent) return null
              const childEl = wrap(`${mod.id}:${route.path}/${child.path || 'index'}`, ChildComponent)
              return child.index
                ? <Route key={`${mod.id}-${route.path}-index`} index element={childEl} />
                : <Route key={`${mod.id}-${route.path}-${child.path}`} path={child.path} element={childEl} />
            })}
          </Route>
        )
      } else {
        internalRoutes.push(
          <Route key={`${mod.id}-${route.path}`} path={route.path} element={element} />
        )
      }
    }
  }

  return { internalRoutes, publicRoutes, portalRoutes }
}

export default function App() {
  const { user, userDoc, loading, isRealCliente, isAdmin } = useAuth()
  const location = useLocation()
  const { modulosVisibles, loading: loadingModulos, permModulosSet } = useModulosVisibles()

  // Report bvO7ePTXSCCk4TLgMcYa: en un cold load de una ruta de módulo
  // (deep-link, refresh, o un <a href> interno que recarga la SPA), mientras
  // el rol resuelve sus permisos `modulosVisibles` viene vacío → las rutas
  // internas no existen todavía y el catch-all `path="*"` rebotaba al inicio.
  // Con sesión activa se espera a que las rutas de módulos existan antes de
  // dejar que el catch-all juzgue la URL. Sin sesión no se espera (el hook
  // resuelve al toque con []; /login y las rutas públicas no dependen de esto).
  if (loading || (user && loadingModulos)) return <FullSpinner />

  // Paso 4 de Eventos (21-sep-2026): un link de mail abierto sin sesión vuelve
  // a su destino después de entrar. El guard (o el catch-all, que es por donde
  // pasa una ruta de módulo sin sesión: sin `userDoc` no hay módulos visibles y
  // la ruta no existe) deja `state.volverA` en la entrada de `/login`;
  // `destinoTrasLogin` sólo acepta rutas internas (`src/core/lib/rutas.js`).
  const afterLogin = isRealCliente() ? '/portal' : '/'
  const trasLogin = destinoTrasLogin(location.state?.volverA, afterLogin)
  const { internalRoutes, publicRoutes, portalRoutes } = buildModuleRoutes(modulosVisibles, (codigo) => veCodigoDeModulo(userDoc, permModulosSet, codigo))

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={trasLogin} replace /> : <LoginPage />} />
      <Route path="/registro" element={user ? <Navigate to={trasLogin} replace /> : <Suspense fallback={<FullSpinner />}><RegistroPage /></Suspense>} />
      {/* Setear contraseña inicial — pública, se renderiza esté o no logueado
          (quien abre el link puede no ser el usuario logueado en ese browser). */}
      <Route path="/setup-password" element={<Suspense fallback={<FullSpinner />}><SetupPasswordPage /></Suspense>} />
      {/* Acciones de los mails de Auth — pública por el mismo motivo: el link
          lo abre la casilla, no la sesión que haya en ese browser. */}
      <Route path="/auth/accion" element={<Suspense fallback={<FullSpinner />}><AccionAuthPage /></Suspense>} />

      {/* Rutas públicas de módulos (ej: /conectar-ml/:token) */}
      {publicRoutes}

      {/* Portal Cliente */}
      <Route path="/portal" element={
        <ClienteRoute>
          <Suspense fallback={<FullSpinner />}>
            <PortalLayout />
          </Suspense>
        </ClienteRoute>
      }>
        {/* Cada página del portal con su ErrorBoundary (wrap()), como las internas:
            sin él, un crash de render o un chunk viejo dejaba la vista en negro. */}
        <Route index element={wrap('portal:core:inicio', PortalHomePage)} />
        <Route path="trabajo" element={wrap('portal:core:trabajo', PortalTrabajoPage)} />
        <Route path="productos" element={wrap('portal:core:productos', PortalProductosPage)} />
        <Route path="cuadro-general" element={wrap('portal:core:cuadro-general', PortalCuadroGeneralPage)} />
        <Route path="documentos" element={wrap('portal:core:documentos', PortalDocumentosPage)} />
        {/* Club Milbrands — vista del miembro. El gate por plan y permiso lo
            hace el sidebar del portal; la ruta existe igual para poder linkear
            desde un mail o una notificación. */}
        <Route path="formacion" element={wrap('portal:core:formacion', PortalFormacionPage)} />
        <Route path="reportes" element={wrap('portal:core:reportes', PortalReportesPage)} />
        <Route path="estudios" element={wrap('portal:core:estudios', PortalEstudiosPage)} />
        <Route path="mensajes" element={wrap('portal:core:mensajes', PortalMensajesPage)} />
        <Route path="mi-equipo" element={wrap('portal:core:mi-equipo', PortalMiEquipoPage)} />
        <Route path="mis-empresas" element={wrap('portal:core:mis-empresas', PortalEmpresasPage)} />
        {/* #73 — Mi cuenta reutiliza la misma página que el staff (es autocontenida) */}
        <Route path="mi-cuenta" element={wrap('portal:core:mi-cuenta', MiCuentaPage)} />
        {/* #49 B9.2-c.2 — Usuarios desde el portal cliente. UsuariosPage detecta
            tipo:'cliente' y restringe la UI (solo su empresa, solo roles client-safe). */}
        <Route path="usuarios" element={wrap('portal:core:usuarios', UsuariosPage)} />
        {/* Paso 5: las páginas del miembro del Club (`enPortal: true` en el config del módulo). */}
        {portalRoutes}
      </Route>

      {/* App interna (consultores) — rutas de módulos generadas dinámicamente.
          SEC-B3 (mayo 2026): MFAGate intercepta a quien trae el claim
          `mfaRequired` (staff: functions/auth/mfaPolicy.js) sin segundo factor enrollado y los redirige a enrollment forzado
          antes de dejarlos entrar al Layout. */}
      <Route path="/" element={
        <ConsultorRoute>
          <MFAGate>
            <Layout />
          </MFAGate>
        </ConsultorRoute>
      }>
        {/* Core routes (siempre presentes).
            Van por wrap(), igual que las rutas de módulos: sin ErrorBoundary,
            un crash de render o un chunk viejo (recharts en la Torre de control)
            que no existe tras un deploy no lo atrapa nadie y deja la vista en
            negro hasta refrescar a mano. El ErrorBoundary detecta el chunk viejo
            y fuerza el reload solo. */}
        <Route index element={wrap('core:dashboard', DashboardPage)} />
        <Route path="mi-cuenta" element={wrap('core:mi-cuenta', MiCuentaPage)} />
        <Route path="admin" element={wrap('core:admin', AdminPage)} />
        <Route path="documentacion" element={wrap('core:documentacion', DocumentacionPage)} />

        {/* Rutas de módulos (generadas desde configs) */}
        {internalRoutes}
      </Route>

      <Route path="*" element={user ? <Navigate to={isRealCliente() ? destinoDeCliente(location) : afterLogin} replace /> : <Navigate to="/login" replace state={estadoParaVolver(location)} />} />
    </Routes>
  )
}

// validation: telemetry pipeline end-to-end (Bloque 4.2 — 2026-05-29)
